<script setup lang="ts">
import { ref, computed } from "vue";
import { useTranslation } from "i18next-vue";
import { useInstallPrompt } from "@/composable/useInstallPrompt";
import InstallAppButton from "@/components/pwa/InstallAppButton.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { currentBrandAppIcon } from "@/brand";

const { t } = useTranslation();
const { canOfferInstall } = useInstallPrompt();

const dismissed = ref(
  $ls.getBooleanItem(LocalStorageKey.INSTALL_BANNER_DISMISSED)
);
const visible = computed(() => canOfferInstall.value && !dismissed.value);

const dismiss = () => {
  dismissed.value = true;
  try {
    $ls.setBooleanItem(LocalStorageKey.INSTALL_BANNER_DISMISSED, true);
  } catch {
    /* stockage indisponible : masqué pour la session en cours */
  }
};
</script>

<template>
  <Transition name="install-banner">
    <div
      v-if="visible"
      class="install-banner d-flex align-items-center gap-3 p-3"
      role="region"
      :aria-label="t('install.bannerTitle')"
    >
      <div class="install-banner__icon">
        <img :src="currentBrandAppIcon" alt="" width="44" height="44" />
      </div>

      <div class="install-banner__body flex-grow-1">
        <p class="install-banner__title">{{ t("install.bannerTitle") }}</p>
        <p class="install-banner__text">{{ t("install.bannerText") }}</p>
      </div>

      <div class="install-banner__actions">
        <InstallAppButton :label="t('install.bannerCta')" />
      </div>

      <button
        type="button"
        class="install-banner__close"
        :aria-label="t('install.dismiss')"
        :title="t('install.dismiss')"
        @click="dismiss"
      >
        <PhosphorIcon name="x" :size="16" weight="bold" />
      </button>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.install-banner {
  position: fixed;
  z-index: 2900; // sous la popup de mise à jour (9999)
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
  width: min(94vw, 480px);
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.18),
    0 2px 8px rgba(0, 0, 0, 0.08);

  &__icon {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid var(--bs-border-color);

    img {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__body {
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.2;
    color: var(--bs-body-color);
  }

  &__text {
    margin: 2px 0 0;
    font-size: 0.75rem;
    line-height: 1.3;
    color: var(--bs-secondary-color);
  }

  &__actions {
    flex-shrink: 0;
  }

  // Croix flottante au-dessus, coin haut-droit de la barre.
  &__close {
    position: absolute;
    top: -10px;
    right: -10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid var(--bs-border-color);
    background-color: var(--bs-body-bg);
    color: var(--bs-body-color);
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);

    &:hover {
      background-color: var(--bs-secondary-bg);
    }
  }
}

// Très petits écrans : passe le bouton sous le texte.
@media (max-width: 380px) {
  .install-banner {
    flex-wrap: wrap;

    &__body {
      flex-basis: calc(100% - 44px - 1rem);
    }

    &__actions {
      flex-basis: 100%;
      display: flex;
      justify-content: flex-end;
    }
  }
}

// Animation d'entrée/sortie (slide-up).
.install-banner-enter-active,
.install-banner-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.install-banner-enter-from,
.install-banner-leave-to {
  opacity: 0;
  transform: translate(-50%, 16px);
}

@media (prefers-reduced-motion: reduce) {
  .install-banner-enter-active,
  .install-banner-leave-active {
    transition: none;
  }
}
</style>
